/**
 * Ancillary — SiteVisit / SurveyJob / GradingJob. Admin + Staff (route-gated).
 *
 * CHAT-FIRST CORRECTION BATCH (2026-05-18):
 *  · A chat surface is the PRIMARY way to raise an ancillary (ChatSurface,
 *    lib/localChat — same model as the Project Conversation). The fee
 *    forms become the manual FALLBACK beneath chat.
 *  · STANDALONE vs ATTACH-TO-PROJECT choice + a project picker when
 *    attaching (FULLSCOPE_ANCILLARY: an ancillary is NOT auto-part-of-
 *    every-project). Billing rule, resolved by the pure
 *    resolveAncillaryBilling(): attached → bills the project's client;
 *    standalone → bills a standalone customer.
 *
 * Fee compute / validator / free-tier counter stay the PURE lib/ancillary
 * (transcribed verbatim from FULLSCOPE_ANCILLARY) — this screen renders.
 */
import { useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { visibilityFor } from '../lib/roles';
import ChatSurface from '../components/ChatSurface';
import { Box, Btn3, Card, Tag } from '../components/ui';
import { inr, attachableProjects, knownClients } from '../lib/trackDemo';
import {
  freeOfflineRemaining,
  quoteGrading,
  quoteSiteVisit,
  quoteSurvey,
  resolveAncillaryBilling,
  validateGradeability,
  type AncillaryAttachMode,
  type AttachableProject,
  type GradingOverlay,
  type SurveyScenario,
  type VisitLocation,
  type VisitMode,
} from '../lib/ancillary';

type Tab = 'site_visit' | 'survey' | 'grading';

/**
 * The projects offered in the picker come from the SINGLE canonical
 * project source on this branch (trackDemo.attachableProjects) — not a
 * demo-only literal duplicated here. When the project-CRUD sprint lands a
 * live Firestore `projects` listing, only that accessor changes; this
 * screen does not. */
const PICKER_PROJECTS: AttachableProject[] = attachableProjects();
/** Real known-client directory — drives the standalone-is-existing-client
 *  rule (Round 2, owner-resolved). Same canonical source. */
const KNOWN_CLIENTS = knownClients();

export default function Ancillary() {
  const { role, user } = useAuth();
  const vis = visibilityFor(role);
  const [tab, setTab] = useState<Tab>('site_visit');

  /* ── Standalone vs attach-to-project (shared across all 3 kinds) ── */
  const [mode, setMode] = useState<AncillaryAttachMode>('attached');
  const [pid, setPid] = useState<string>(PICKER_PROJECTS[0]?.pid ?? '');
  const [custName, setCustName] = useState('');
  const [custPhone, setCustPhone] = useState('');

  const project = PICKER_PROJECTS.find((p) => p.pid === pid) ?? null;

  let billing: ReturnType<typeof resolveAncillaryBilling> | null = null;
  let billingError: string | null = null;
  try {
    billing = resolveAncillaryBilling({
      mode,
      project: mode === 'attached' ? project : null,
      customer:
        mode === 'standalone'
          ? { name: custName, phone: custPhone }
          : null,
      knownClients: KNOWN_CLIENTS,
    });
  } catch (e) {
    billingError = (e as Error).message;
  }

  /** The attached tier drives the fee compute (free-tier, L1A block …). */
  const attachedTier = mode === 'attached' ? (project?.tier ?? null) : null;

  function applyChatPatch(patch: Record<string, unknown>) {
    if (patch.ancillary === 'site_visit') setTab('site_visit');
    if (patch.ancillary === 'survey') setTab('survey');
    if (patch.ancillary === 'grading') setTab('grading');
    if (patch.action === 'standalone') setMode('standalone');
    if (patch.action === 'attach') setMode('attached');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* PRIMARY: chat-first */}
      <ChatSurface
        title="Raise an ancillary"
        note="Chat is primary · the fee forms below are the manual fallback"
        authorName={user?.displayName || user?.email?.split('@')[0] || 'You'}
        isTeamView={vis.seesReviewLayer}
        onApply={applyChatPatch}
        surface="ancillary"
        formState={{ mode, tab, pid }}
      />

      {/* Standalone vs attach-to-project + project picker */}
      <Box
        title="Bill to — standalone or attach to a project"
        note="An ancillary is NOT auto-part-of-every-project (FULLSCOPE_ANCILLARY)"
      >
        <div style={fieldRow}>
          <label style={radioLbl}>
            <input
              type="radio"
              name="anc-mode"
              checked={mode === 'attached'}
              onChange={() => setMode('attached')}
            />{' '}
            Attach to a project
          </label>
          <label style={radioLbl}>
            <input
              type="radio"
              name="anc-mode"
              checked={mode === 'standalone'}
              onChange={() => setMode('standalone')}
            />{' '}
            Standalone (no project)
          </label>
        </div>

        {mode === 'attached' ? (
          <div style={{ ...fieldRow, marginTop: 12 }}>
            <label style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
              Project:{' '}
              <select
                style={sel}
                value={pid}
                onChange={(e) => setPid(e.target.value)}
              >
                {PICKER_PROJECTS.map((p) => (
                  <option key={p.pid} value={p.pid}>
                    {p.pid} · {p.clientName} ({p.clientNo}) · {p.tier}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <div style={{ ...fieldRow, marginTop: 12 }}>
            <input
              style={{ ...sel, minWidth: 200 }}
              placeholder="Standalone customer name"
              value={custName}
              onChange={(e) => setCustName(e.target.value)}
            />
            <input
              style={{ ...sel, minWidth: 160 }}
              placeholder="Phone (optional)"
              value={custPhone}
              onChange={(e) => setCustPhone(e.target.value)}
            />
          </div>
        )}

        <Card flat className="row-card" style={{ marginTop: 14 }}>
          {billing ? (
            <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
              <Tag kind="val">
                Bills:{' '}
                {billing.billTo.kind === 'project_client'
                  ? "the project's client"
                  : 'a standalone customer'}
              </Tag>{' '}
              <b style={{ color: 'var(--ink)' }}>{billing.billTo.name}</b>
              <br />
              <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                {billing.summary}
              </span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--warn)' }}>
              <Tag kind="warn">Incomplete</Tag> {billingError}
            </div>
          )}
        </Card>
      </Box>

      {/* FALLBACK: the fee forms */}
      <Box
        title="Ancillary fee (fallback form)"
        note="Site Visit · Survey & Map Making · Map Grading — LOCKED fee compute"
      >
        <div className="fallback-form">
          <div className="fb-flag">Manual fallback — use only if chat can't</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {(
              [
                ['site_visit', 'Site Visit'],
                ['survey', 'Survey & Map Making'],
                ['grading', 'Map Grading'],
              ] as Array<[Tab, string]>
            ).map(([k, label]) => (
              <button
                key={k}
                className={`pill ${tab === k ? 'on' : ''}`}
                onClick={() => setTab(k)}
              >
                {label}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            {tab === 'site_visit' && (
              <SiteVisitForm attachedTier={attachedTier} billable={!!billing} />
            )}
            {tab === 'survey' && <SurveyForm billable={!!billing} />}
            {tab === 'grading' && <GradingForm billable={!!billing} />}
          </div>
        </div>
      </Box>
    </div>
  );
}

function FeeBlock({
  lines,
  subtotal,
  travel,
  notes,
}: {
  lines: { label: string; amountINR: number }[];
  subtotal: number;
  travel: boolean;
  notes: string[];
}) {
  return (
    <div style={{ marginTop: 14 }}>
      {lines.map((l, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 13,
            padding: '6px 0',
            borderBottom: '1px solid var(--rule)',
          }}
        >
          <span style={{ color: 'var(--ink-soft)' }}>{l.label}</span>
          <b>{inr(l.amountINR)}</b>
        </div>
      ))}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 10,
          fontFamily: 'var(--font-display)',
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        <span>Subtotal{travel ? ' (+ travel actuals)' : ''}</span>
        <span>{inr(subtotal)}</span>
      </div>
      {notes.map((n, i) => (
        <p key={i} style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 8 }}>
          {n}
        </p>
      ))}
    </div>
  );
}

const sel: React.CSSProperties = {
  font: '600 13px/1 var(--font-ui)',
  padding: '8px 10px',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  background: 'var(--surface)',
  color: 'var(--ink)',
};
const fieldRow: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
  alignItems: 'center',
};
const radioLbl: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--ink-soft)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
};

function SiteVisitForm({
  attachedTier,
  billable,
}: {
  attachedTier: string | null;
  billable: boolean;
}) {
  const [mode, setMode] = useState<VisitMode>('offline');
  const [loc, setLoc] = useState<VisitLocation>('local');
  const [freeUsed, setFreeUsed] = useState(0);
  const q = useMemo(
    () =>
      quoteSiteVisit({
        mode,
        location: loc,
        attachedTier,
        freeOfflineUsed: freeUsed,
      }),
    [mode, loc, attachedTier, freeUsed],
  );
  const remaining = freeOfflineRemaining(attachedTier, {
    freeOfflineUsed: freeUsed,
  });
  return (
    <Box title="Raise a Site Visit" note="§1 — fee compute LOCKED">
      <p style={{ fontSize: 12, color: 'var(--ink-mute)', marginBottom: 10 }}>
        Attached tier:{' '}
        <b>{attachedTier ?? 'standalone (no tier)'}</b>
      </p>
      <div style={fieldRow}>
        <select style={sel} value={mode} onChange={(e) => setMode(e.target.value as VisitMode)}>
          <option value="offline">Offline (physical)</option>
          <option value="online">Online (= ½ offline)</option>
        </select>
        <select style={sel} value={loc} onChange={(e) => setLoc(e.target.value as VisitLocation)}>
          <option value="local">Local</option>
          <option value="outstation">Outstation</option>
        </select>
        <label style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
          Free offline used:{' '}
          <input
            type="number"
            min={0}
            value={freeUsed}
            onChange={(e) => setFreeUsed(Number(e.target.value))}
            style={{ ...sel, width: 56 }}
          />
        </label>
      </div>
      <p style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 10 }}>
        Free local-offline visits remaining on this engagement:{' '}
        <b>{remaining}</b>
      </p>
      {q.blocked ? (
        <Card flat className="row-card" style={{ marginTop: 14 }}>
          <Tag kind="warn">Blocked</Tag> {q.notes[0]}
        </Card>
      ) : (
        <FeeBlock
          lines={q.lines}
          subtotal={q.subtotalINR}
          travel={q.travelActualsExtra}
          notes={[
            q.coveredByFreeTier ? 'Covered by the free-tier allowance.' : '',
            ...q.notes,
          ].filter(Boolean)}
        />
      )}
      <div style={{ marginTop: 14 }}>
        <Btn3 intent="ink" disabled={q.blocked || !billable}>
          Quote & schedule (money-before-delivery)
        </Btn3>
        {!billable && (
          <p style={{ fontSize: 12, color: 'var(--warn)', marginTop: 8 }}>
            Set a valid bill-to (project or standalone customer) above first.
          </p>
        )}
      </div>
    </Box>
  );
}

function SurveyForm({ billable }: { billable: boolean }) {
  const [scenario, setScenario] = useState<SurveyScenario>('A');
  const [area, setArea] = useState(1500);
  const [floors, setFloors] = useState(2);
  const q = useMemo(
    () =>
      quoteSurvey({
        scenario,
        areaSqft: area,
        gridFloors: scenario === 'A' ? undefined : floors,
      }),
    [scenario, area, floors],
  );
  return (
    <Box title="Raise a Survey & Map-Making job" note="§2 — pricing LOCKED">
      <div style={fieldRow}>
        <select
          style={sel}
          value={scenario}
          onChange={(e) => setScenario(e.target.value as SurveyScenario)}
        >
          <option value="A">A — AV measures (₹3.50/sqft)</option>
          <option value="B">B — client measured (grid-only)</option>
          <option value="C">C — sketch → draft (₹3.00/sqft)</option>
        </select>
        <label style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
          Area sqft:{' '}
          <input
            type="number"
            value={area}
            onChange={(e) => setArea(Number(e.target.value))}
            style={{ ...sel, width: 90 }}
          />
        </label>
        {scenario !== 'A' && (
          <label style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
            Grid floors:{' '}
            <input
              type="number"
              min={0}
              value={floors}
              onChange={(e) => setFloors(Number(e.target.value))}
              style={{ ...sel, width: 56 }}
            />
          </label>
        )}
      </div>
      <FeeBlock
        lines={q.lines}
        subtotal={q.subtotalINR}
        travel={q.travelActualsExtra}
        notes={q.notes}
      />
      <div style={{ marginTop: 14 }}>
        <Btn3 intent="ink" disabled={!billable}>
          Quote & start (money-before-delivery)
        </Btn3>
      </div>
    </Box>
  );
}

function GradingForm({ billable }: { billable: boolean }) {
  const [overlay, setOverlay] = useState<GradingOverlay>('full');
  const [floors, setFloors] = useState(2);
  const [devtaMarma, setDevtaMarma] = useState(false);
  const [astro, setAstro] = useState(0);
  const [hasScale, setHasScale] = useState(true);
  const [hasNorth, setHasNorth] = useState(true);
  const [isDim, setIsDim] = useState(true);
  const grade = useMemo(
    () => validateGradeability({ hasScale, hasNorth, isDimensioned: isDim }),
    [hasScale, hasNorth, isDim],
  );
  const q = useMemo(
    () =>
      quoteGrading({
        floors,
        overlay,
        addDevtaMarma: devtaMarma,
        astroCharts: astro,
      }),
    [floors, overlay, devtaMarma, astro],
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Box title="Gradeability check" note="§3.3 — auto-route to Survey Scenario C if not gradeable">
        <div style={fieldRow}>
          <label><input type="checkbox" checked={hasScale} onChange={(e) => setHasScale(e.target.checked)} /> stated scale</label>
          <label><input type="checkbox" checked={hasNorth} onChange={(e) => setHasNorth(e.target.checked)} /> north reference</label>
          <label><input type="checkbox" checked={isDim} onChange={(e) => setIsDim(e.target.checked)} /> dimensioned</label>
        </div>
        <Card flat className="row-card" style={{ marginTop: 12 }}>
          {grade.gradeable ? <Tag>gradeable</Tag> : <Tag kind="warn">route → Survey C</Tag>}{' '}
          <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{grade.message}</span>
        </Card>
      </Box>
      <Box title="Raise a Map-Grading job" note="§3 — pricing LOCKED · plot grid free">
        <div style={fieldRow}>
          <select
            style={sel}
            value={overlay}
            onChange={(e) => setOverlay(e.target.value as GradingOverlay)}
          >
            <option value="full">Full set (₹2,500/floor)</option>
            <option value="subset_16_bar">16-div + bar (₹1,000/floor)</option>
          </select>
          {overlay === 'subset_16_bar' && (
            <label>
              <input
                type="checkbox"
                checked={devtaMarma}
                onChange={(e) => setDevtaMarma(e.target.checked)}
              />{' '}
              + Devta + Marma (₹1,500/floor)
            </label>
          )}
          <label style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
            Floors:{' '}
            <input
              type="number"
              min={1}
              value={floors}
              onChange={(e) => setFloors(Number(e.target.value))}
              style={{ ...sel, width: 56 }}
            />
          </label>
          <label style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
            Astro charts:{' '}
            <input
              type="number"
              min={0}
              value={astro}
              onChange={(e) => setAstro(Number(e.target.value))}
              style={{ ...sel, width: 56 }}
            />
          </label>
        </div>
        <FeeBlock
          lines={q.lines}
          subtotal={q.subtotalINR}
          travel={q.travelActualsExtra}
          notes={q.notes}
        />
        <div style={{ marginTop: 14 }}>
          <Btn3 intent="ink" disabled={!grade.gradeable || !billable}>
            Quote & start (money-before-delivery)
          </Btn3>
        </div>
      </Box>
    </div>
  );
}
